import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import Button from '../components/Button';
import { getToken } from '../services/auth';

interface ProfileFormData {
  name: string;
  given_name: string;
  family_name: string;
  nickname: string;
  user_metadata: {
    countryCode: string;
    designation: string;
    dob: string;
    phoneNumber: string;
    address1: string;
    address2: string;
    country: string;
    state: string;
    zip: string;
    note: string;
  };
}

const ProfilePage: React.FC = () => {
  const { user, updateProfile } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);
  const [showToken, setShowToken] = useState(false);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [formData, setFormData] = useState<ProfileFormData>({
    name: '',
    given_name: '',
    family_name: '',
    nickname: '',
    user_metadata: {
      countryCode: '',
      designation: '',
      dob: '',
      phoneNumber: '',
      address1: '',
      address2: '',
      country: '',
      state: '',
      zip: '',
      note: ''
    }
  });

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        given_name: user.given_name || '',
        family_name: user.family_name || '',
        nickname: user.nickname || '',
        user_metadata: {
          countryCode: user.user_metadata?.countryCode || '',
          designation: user.user_metadata?.designation || '',
          dob: user.user_metadata?.dob || '',
          phoneNumber: user.user_metadata?.phoneNumber || '',
          address1: user.user_metadata?.address1 || '',
          address2: user.user_metadata?.address2 || '',
          country: user.user_metadata?.country || '',
          state: user.user_metadata?.state || '',
          zip: user.user_metadata?.zip || '',
          note: user.user_metadata?.note || ''
        }
      });
    }
  }, [user]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    if (name.startsWith('user_metadata.')) {
      const metadataField = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        user_metadata: {
          ...prev.user_metadata,
          [metadataField]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);

    try {
      // Format the data for Auth0 update
      const updateData = {
        name: formData.name,
        given_name: formData.given_name,
        family_name: formData.family_name,
        nickname: formData.nickname,
        user_metadata: {
          countryCode: formData.user_metadata.countryCode,
          designation: formData.user_metadata.designation,
          dob: formData.user_metadata.dob,
          phoneNumber: formData.user_metadata.phoneNumber,
          address1: formData.user_metadata.address1,
          address2: formData.user_metadata.address2,
          country: formData.user_metadata.country,
          state: formData.user_metadata.state,
          zip: formData.user_metadata.zip,
          note: formData.user_metadata.note
        }
      };

      const success = await updateProfile(updateData);
      if (success) {
        setMessage({ text: 'Profile updated successfully', type: 'success' });
        setIsEditing(false);
      } else {
        setMessage({ text: 'Failed to update profile', type: 'error' });
      }
    } catch (error) {
      console.error('Profile update error:', error);
      setMessage({ text: 'An error occurred while updating profile', type: 'error' });
    }
  };

  if (!user) {
    return <div>Loading...</div>;
  }

  return (
    <div className="profile-page" style={{
      maxWidth: '600px',
      margin: '0 auto',
      padding: '2rem',
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
    }}>
      <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
        {user.picture && (
          <img
            src={user.picture}
            alt="Profile"
            style={{
              width: '120px',
              height: '120px',
              borderRadius: '60px',
              marginBottom: '1rem'
            }}
          />
        )}
        <h1 style={{ margin: '0', color: '#333' }}>Profile</h1>
      </div>

      {message && (
        <div style={{
          padding: '1rem',
          marginBottom: '1rem',
          borderRadius: '4px',
          backgroundColor: message.type === 'success' ? '#e6ffe6' : '#ffe6e6',
          color: message.type === 'success' ? '#006600' : '#cc0000'
        }}>
          {message.text}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', color: '#666' }}>
            Email
          </label>
          <input
            type="email"
            value={user.email || ''}
            disabled
            style={{
              width: '100%',
              padding: '0.5rem',
              border: '1px solid #ddd',
              borderRadius: '4px',
              backgroundColor: '#f5f5f5'
            }}
          />
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', color: '#666' }}>
            Full Name
          </label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            disabled={!isEditing}
            style={{
              width: '100%',
              padding: '0.5rem',
              border: '1px solid #ddd',
              borderRadius: '4px',
              backgroundColor: isEditing ? 'white' : '#f5f5f5'
            }}
          />
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', color: '#666' }}>
            First Name
          </label>
          <input
            type="text"
            name="given_name"
            value={formData.given_name}
            onChange={handleInputChange}
            disabled={!isEditing}
            style={{
              width: '100%',
              padding: '0.5rem',
              border: '1px solid #ddd',
              borderRadius: '4px',
              backgroundColor: isEditing ? 'white' : '#f5f5f5'
            }}
          />
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', color: '#666' }}>
            Last Name
          </label>
          <input
            type="text"
            name="family_name"
            value={formData.family_name}
            onChange={handleInputChange}
            disabled={!isEditing}
            style={{
              width: '100%',
              padding: '0.5rem',
              border: '1px solid #ddd',
              borderRadius: '4px',
              backgroundColor: isEditing ? 'white' : '#f5f5f5'
            }}
          />
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', color: '#666' }}>
            Nickname
          </label>
          <input
            type="text"
            name="nickname"
            value={formData.nickname}
            onChange={handleInputChange}
            disabled={!isEditing}
            style={{
              width: '100%',
              padding: '0.5rem',
              border: '1px solid #ddd',
              borderRadius: '4px',
              backgroundColor: isEditing ? 'white' : '#f5f5f5'
            }}
          />
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', color: '#666' }}>
            Designation
          </label>
          <input
            type="text"
            name="user_metadata.designation"
            value={formData.user_metadata.designation}
            onChange={handleInputChange}
            disabled={!isEditing}
            style={{
              width: '100%',
              padding: '0.5rem',
              border: '1px solid #ddd',
              borderRadius: '4px',
              backgroundColor: isEditing ? 'white' : '#f5f5f5'
            }}
          />
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', color: '#666' }}>
            Date of Birth
          </label>
          <input
            type="date"
            name="user_metadata.dob"
            value={formData.user_metadata.dob.split('T')[0]}
            onChange={handleInputChange}
            disabled={!isEditing}
            style={{
              width: '100%',
              padding: '0.5rem',
              border: '1px solid #ddd',
              borderRadius: '4px',
              backgroundColor: isEditing ? 'white' : '#f5f5f5'
            }}
          />
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', color: '#666' }}>
            Phone Number
          </label>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <input
              type="text"
              name="user_metadata.countryCode"
              value={formData.user_metadata.countryCode}
              onChange={handleInputChange}
              disabled={!isEditing}
              placeholder="Code"
              style={{
                width: '80px',
                padding: '0.5rem',
                border: '1px solid #ddd',
                borderRadius: '4px',
                backgroundColor: isEditing ? 'white' : '#f5f5f5'
              }}
            />
            <input
              type="tel"
              name="user_metadata.phoneNumber"
              value={formData.user_metadata.phoneNumber}
              onChange={handleInputChange}
              disabled={!isEditing}
              style={{
                flex: 1,
                padding: '0.5rem',
                border: '1px solid #ddd',
                borderRadius: '4px',
                backgroundColor: isEditing ? 'white' : '#f5f5f5'
              }}
            />
          </div>
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', color: '#666' }}>
            Address Line 1
          </label>
          <input
            type="text"
            name="user_metadata.address1"
            value={formData.user_metadata.address1}
            onChange={handleInputChange}
            disabled={!isEditing}
            style={{
              width: '100%',
              padding: '0.5rem',
              border: '1px solid #ddd',
              borderRadius: '4px',
              backgroundColor: isEditing ? 'white' : '#f5f5f5'
            }}
          />
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', color: '#666' }}>
            Address Line 2
          </label>
          <input
            type="text"
            name="user_metadata.address2"
            value={formData.user_metadata.address2}
            onChange={handleInputChange}
            disabled={!isEditing}
            style={{
              width: '100%',
              padding: '0.5rem',
              border: '1px solid #ddd',
              borderRadius: '4px',
              backgroundColor: isEditing ? 'white' : '#f5f5f5'
            }}
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', color: '#666' }}>
              Country
            </label>
            <input
              type="text"
              name="user_metadata.country"
              value={formData.user_metadata.country}
              onChange={handleInputChange}
              disabled={!isEditing}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid #ddd',
                borderRadius: '4px',
                backgroundColor: isEditing ? 'white' : '#f5f5f5'
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', color: '#666' }}>
              State
            </label>
            <input
              type="text"
              name="user_metadata.state"
              value={formData.user_metadata.state}
              onChange={handleInputChange}
              disabled={!isEditing}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid #ddd',
                borderRadius: '4px',
                backgroundColor: isEditing ? 'white' : '#f5f5f5'
              }}
            />
          </div>
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', color: '#666' }}>
            ZIP Code
          </label>
          <input
            type="text"
            name="user_metadata.zip"
            value={formData.user_metadata.zip}
            onChange={handleInputChange}
            disabled={!isEditing}
            style={{
              width: '100%',
              padding: '0.5rem',
              border: '1px solid #ddd',
              borderRadius: '4px',
              backgroundColor: isEditing ? 'white' : '#f5f5f5'
            }}
          />
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', color: '#666' }}>
            Notes
          </label>
          <textarea
            name="user_metadata.note"
            value={formData.user_metadata.note}
            onChange={handleInputChange}
            disabled={!isEditing}
            style={{
              width: '100%',
              padding: '0.5rem',
              border: '1px solid #ddd',
              borderRadius: '4px',
              backgroundColor: isEditing ? 'white' : '#f5f5f5',
              minHeight: '100px',
              resize: 'vertical'
            }}
          />
        </div>

        <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem', marginTop: '2rem' }}>
          {!isEditing ? (
            <Button onClick={() => setIsEditing(true)}>Edit Profile</Button>
          ) : (
            <>
              <Button type="submit">Save Changes</Button>
              <Button onClick={() => {
                setIsEditing(false);
                setMessage(null);
                if (user) {
                  setFormData({
                    name: user.name || '',
                    given_name: user.given_name || '',
                    family_name: user.family_name || '',
                    nickname: user.nickname || '',
                    user_metadata: {
                      countryCode: user.user_metadata?.countryCode || '',
                      designation: user.user_metadata?.designation || '',
                      dob: user.user_metadata?.dob || '',
                      phoneNumber: user.user_metadata?.phoneNumber || '',
                      address1: user.user_metadata?.address1 || '',
                      address2: user.user_metadata?.address2 || '',
                      country: user.user_metadata?.country || '',
                      state: user.user_metadata?.state || '',
                      zip: user.user_metadata?.zip || '',
                      note: user.user_metadata?.note || ''
                    }
                  });
                }
              }}>Cancel</Button>
            </>
          )}
        </div>
      </form>

      {/* Token Section */}
      <div style={{ marginTop: '2rem', textAlign: 'center' }}>
        <Button 
          onClick={async () => {
            const token = await getToken();
            setAccessToken(token);
            setShowToken(!showToken);
          }}
        >
          {showToken ? 'Hide Token' : 'Show Token'}
        </Button>
      </div>
      
      {showToken && accessToken && (
        <div style={{ 
          marginTop: '1rem',
          backgroundColor: '#f5f5f5', 
          padding: '1rem', 
          borderRadius: '4px',
          wordBreak: 'break-all',
          fontFamily: 'monospace',
          fontSize: '0.9em'
        }}>
          <div>
            <strong>Bearer Token:</strong> Bearer {accessToken}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfilePage;
